/** Grid used by floor layout drag + dimension snapping */
export const MAP_GRID = 8

export const MAP_W_MIN = 64
export const MAP_W_MAX = 300
export const MAP_H_MIN = 52
export const MAP_H_MAX = 200

export type MapShape = 'rectangle' | 'square' | 'round'

export function snapMapToGrid(value: number, grid: number = MAP_GRID): number {
  return Math.max(grid, Math.round(value / grid) * grid)
}

/**
 * Suggested footprint (px) from seat count and shape. Monotonic in seats for rectangles;
 * square/round use a single side length.
 */
export function suggestMapDimensions(
  seats: number,
  shape: MapShape,
  grid: number = MAP_GRID
): { map_w: number; map_h: number } {
  const s = Math.max(1, Math.min(20, Math.round(seats) || 1))

  if (shape === 'square' || shape === 'round') {
    const side = snapMapToGrid(Math.min(MAP_W_MAX, Math.max(MAP_W_MIN, 56 + s * 10)), grid)
    return { map_w: side, map_h: side }
  }

  const rawW = 72 + s * 14
  const rawH = 48 + Math.min(s, 12) * 5
  const map_w = snapMapToGrid(clampDim(rawW, MAP_W_MIN, MAP_W_MAX), grid)
  const map_h = snapMapToGrid(clampDim(rawH, MAP_H_MIN, MAP_H_MAX), grid)
  return { map_w, map_h }
}

function clampDim(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n))
}

/** Fallback when DB has no map size (read-only maps). */
export function fallbackMapDimensions(table: {
  seating_capacity?: number
  shape?: string | null
}): { map_w: number; map_h: number } {
  const shape = normalizeShape(table.shape)
  return suggestMapDimensions(table.seating_capacity ?? 4, shape)
}

export function normalizeShape(shape: string | null | undefined): MapShape {
  if (shape === 'square' || shape === 'round') return shape
  return 'rectangle'
}

// ── Floor canvas (the per-floor drawing surface) sizing ────────────────────
// Shared by the admin layout builder (editable) and the read-only server
// floor map so a given floor renders at identical dimensions in both. Before
// this was centralised, the server map hardcoded its own size and the admin's
// per-floor canvas configuration never reached it.

export const DEFAULT_CANVAS_W = 1200
export const DEFAULT_CANVAS_H = 800
export const MIN_CANVAS_W = 640
export const MIN_CANVAS_H = 480
export const MAX_CANVAS_W = 4000
export const MAX_CANVAS_H = 3000

export type FloorDims = { width: number; height: number }

/** Resolve a floor's canvas size from the saved `floor_layouts` map, clamped,
 *  falling back to the default canvas for floors the operator never resized. */
export function resolveFloorDims(
  layouts: Record<string, FloorDims> | undefined,
  floor: string,
): FloorDims {
  const cfg = layouts?.[floor]
  return {
    width: clampDim(cfg?.width ?? DEFAULT_CANVAS_W, MIN_CANVAS_W, MAX_CANVAS_W),
    height: clampDim(cfg?.height ?? DEFAULT_CANVAS_H, MIN_CANVAS_H, MAX_CANVAS_H),
  }
}

/** Bounding box (px) that must be visible for a floor: the union of the
 *  configured canvas and every *placed* table's footprint (incl. a chair pad),
 *  so tables dropped outside the admin canvas are still framed by fit-to-view.
 *  `minX/minY` are the top-left origin (can be negative); `width/height` the span.
 *  Fitting to this — rather than the raw 1200×800 canvas — avoids under-zoom when
 *  tables cluster and guarantees off-canvas tables are never clipped. */
export function contentBounds(
  tables: Array<{
    map_x?: number | null
    map_y?: number | null
    map_w?: number | null
    map_h?: number | null
    seating_capacity?: number
    shape?: string | null
  }>,
  canvas: FloorDims,
  pad = 0,
): { minX: number; minY: number; width: number; height: number } {
  // Seed with the canvas so an empty/near-empty floor still fits its surface.
  let minX = 0
  let minY = 0
  let maxX = canvas.width
  let maxY = canvas.height

  for (const t of tables) {
    const x = Number(t.map_x)
    const y = Number(t.map_y)
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue
    const hasW = t.map_w != null && Number.isFinite(Number(t.map_w)) && Number(t.map_w) > 0
    const hasH = t.map_h != null && Number.isFinite(Number(t.map_h)) && Number(t.map_h) > 0
    const fb = fallbackMapDimensions(t)
    const w = hasW ? Number(t.map_w) : fb.map_w
    const h = hasH ? Number(t.map_h) : fb.map_h
    if (x - pad < minX) minX = x - pad
    if (y - pad < minY) minY = y - pad
    if (x + w + pad > maxX) maxX = x + w + pad
    if (y + h + pad > maxY) maxY = y + h + pad
  }

  return { minX, minY, width: maxX - minX, height: maxY - minY }
}

/** Bounding box (px) of just the *placed* tables (+ chair pad) — ignoring the
 *  empty floor canvas. This is what the live server floor fits to, so a handful
 *  of tables fills the viewport at a comfortable size instead of being shrunk to
 *  fit a mostly-empty 1200×800 surface. Falls back to the `canvas` only when no
 *  table has coordinates yet (so an unlaid floor still has something to frame). */
export function tablesBounds(
  tables: Array<{
    map_x?: number | null
    map_y?: number | null
    map_w?: number | null
    map_h?: number | null
    seating_capacity?: number
    shape?: string | null
  }>,
  canvas: FloorDims,
  pad = 0,
): { minX: number; minY: number; width: number; height: number } {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const t of tables) {
    const x = Number(t.map_x)
    const y = Number(t.map_y)
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue
    const hasW = t.map_w != null && Number.isFinite(Number(t.map_w)) && Number(t.map_w) > 0
    const hasH = t.map_h != null && Number.isFinite(Number(t.map_h)) && Number(t.map_h) > 0
    const fb = fallbackMapDimensions(t)
    const w = hasW ? Number(t.map_w) : fb.map_w
    const h = hasH ? Number(t.map_h) : fb.map_h
    if (x - pad < minX) minX = x - pad
    if (y - pad < minY) minY = y - pad
    if (x + w + pad > maxX) maxX = x + w + pad
    if (y + h + pad > maxY) maxY = y + h + pad
  }

  // No placed tables — frame the canvas so an unlaid floor isn't a void.
  if (!Number.isFinite(minX)) {
    return { minX: 0, minY: 0, width: canvas.width, height: canvas.height }
  }
  return { minX, minY, width: maxX - minX, height: maxY - minY }
}

/** Parse the `floor_layouts` app-setting blob into a validated dimensions map.
 *  Tolerant of missing/garbage values (drops anything non-positive). */
export function parseFloorLayouts(raw: unknown): Record<string, FloorDims> {
  if (!raw || typeof raw !== 'object') return {}
  const out: Record<string, FloorDims> = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v && typeof v === 'object') {
      const w = Number((v as { width?: unknown }).width)
      const h = Number((v as { height?: unknown }).height)
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        out[k] = { width: w, height: h }
      }
    }
  }
  return out
}

export const MAP_SIZE_PRESETS = [
  { label: 'S', map_w: 96, map_h: 64 },
  { label: 'M', map_w: 120, map_h: 72 },
  { label: 'L', map_w: 160, map_h: 88 },
  { label: 'XL', map_w: 200, map_h: 104 },
] as const

export function normalizeRotationDegrees(deg: number): number {
  if (!Number.isFinite(deg)) return 0
  const n = Math.round(deg) % 360
  return n < 0 ? n + 360 : n
}

export function snapRotationToStep(deg: number, step: number): number {
  return normalizeRotationDegrees(Math.round(deg / step) * step)
}
